import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CancelClose,
  ChevronDown,
  ChevronUp,
  ErrorWarningCircle,
  Success,
} from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import {
  PromoCodeDraft,
  PromoCodeErrorField,
  PromoCodeErrors,
  PromoCodeStatus,
} from '../../../stores/types/promoCode.interface';
import { Promotion } from '../../../stores/types/promotions.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  FormInput,
  Icon,
  Label,
  LabelType,
} from '../../_ui';
import { CustomInputType, DateTimePicker } from '../../_ui/date-picker/Date-Picker.component';
import { InputType, ValidationType } from '../../_ui/Input.component';
import { toLocalDateString, toValidDate } from '../promotion.utils';
import { PROMO_CODE_MAX_LENGTH } from './promoCode.constants';
import { hasMaxLength, hasMinLength } from './promoCode.utils';
import PromoCodeStatusPill from './PromoCodeStatusPill.component';

interface ValidationHintProps {
  label: string;
  /** undefined → neutral (not yet evaluated), true → pass, false → fail. */
  satisfied?: boolean;
}

const ValidationHint = ({ label, satisfied }: ValidationHintProps) => {
  // Empty → neutral gray cross, met → green check, not met → red cross.
  const color =
    satisfied === undefined
      ? ColorType.GREY4
      : satisfied
      ? ColorType.POSITIVE1
      : ColorType.NEGATIVE1;

  return (
    <div className='flex flex-row items-center gap-1'>
      <Icon src={satisfied ? Success : CancelClose} size={IconSize.SIZE_16x16} color={color} />
      <Label text={label} type={LabelType.LABEL_XS_MEDIUM} color={color} />
    </div>
  );
};

interface PromoCodeCardProps {
  index: number;
  draft: PromoCodeDraft;
  errors: PromoCodeErrors;
  promotion: Promotion;
  isExpanded: boolean;
  isSaving: boolean;
  /**
   * 'create' (default) drafts a brand-new code; 'edit' edits an existing saved DRAFT code.
   * Edit mode adds a "Cancel" button (via {@link onCancel}) that exits without saving; in
   * both modes {@link onDelete} removes the code (discard local draft vs. delete saved).
   */
  mode?: 'create' | 'edit';
  // Handlers are keyed by the card's own `draft.localId` so the parent can pass
  // referentially-stable callbacks (instead of fresh per-row closures), which is what
  // lets the React.memo wrapper actually skip re-renders of unaffected cards.
  onToggleExpand: (id: string) => void;
  onChange: (id: string, updater: (prev: PromoCodeDraft) => PromoCodeDraft) => void;
  onClearError: (id: string, field: PromoCodeErrorField) => void;
  onSave: (draft: PromoCodeDraft) => void;
  /** Discards a not-yet-saved draft (create mode). */
  onDelete?: (id: string) => void;
  /** Exits edit mode without saving (edit mode). */
  onCancel?: (id: string) => void;
}

const PromoCodeCardComponent = ({
  index,
  draft,
  errors,
  promotion,
  isExpanded,
  isSaving,
  mode = 'create',
  onToggleExpand,
  onChange,
  onClearError,
  onSave,
  onDelete,
  onCancel,
}: PromoCodeCardProps) => {
  const { t } = useTranslation();

  const hasCode = draft.code.trim().length > 0;

  // The code's dates must fall inside the parent promotion's window, so the calendars
  // grey out everything outside it. We also tighten across the two fields: the end
  // picker can't precede the chosen start, and the start picker can't follow the chosen
  // end — so an invalid range can't be picked at all. A null bound = unbounded on that
  // side (e.g. a promotion with no end date).
  const promotionStart = toValidDate(promotion.validFrom);
  const promotionEnd = toValidDate(promotion.validUntil);

  const renderError = (message?: string) =>
    message ? (
      <Label
        className='pt-1'
        type={LabelType.LABEL_S_MEDIUM}
        color={ColorType.NEGATIVE}
        text={message}
      />
    ) : null;

  return (
    <div
      className='border border-grey2 rounded-lg overflow-hidden'
      data-testid={`promoCodeCard-${index}`}
    >
      <button
        type='button'
        className='flex flex-row items-center justify-between w-full px-4 py-3 bg-grey0 rounded-t-lg'
        onClick={() => onToggleExpand(draft.localId)}
        aria-expanded={isExpanded}
        data-testid={`promoCodeCardHeader-${index}`}
      >
        <div className='flex flex-row items-center gap-2'>
          <Label
            text={t('promo_code_index', { index })}
            type={LabelType.LABEL_S}
            color={ColorType.GREY6}
          />
          <Label text={draft.code.trim() || '—'} type={LabelType.LABEL_S} color={ColorType.BLACK} />
          <PromoCodeStatusPill status={PromoCodeStatus.DRAFT} />
        </div>
        <Icon
          src={isExpanded ? ChevronUp : ChevronDown}
          size={IconSize.SIZE_24x24}
          color={ColorType.GREY5}
          alt={isExpanded ? t('icon_alt_collapse') : t('icon_alt_expand')}
        />
      </button>

      {isExpanded && (
        <div className='flex flex-col divide-y divide-grey2 px-4 bg-white'>
          <div className='flex flex-col gap-2 py-5'>
            <div className='flex flex-col gap-1'>
              <Label
                text={t('create_a_unique_code')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
              />
              <Label
                text={t('promo_code_unique_description')}
                type={LabelType.BODY3}
                color={ColorType.GREY6}
              />
            </div>
            <div className='w-full max-w-[320px]'>
              <FormInput
                defaultValue={draft.code}
                placeholder={t('promo_code_placeholder')}
                inputType={InputType.TEXT}
                validationType={ValidationType.NONE}
                maxLen={`${PROMO_CODE_MAX_LENGTH}`}
                width='100%'
                hasError={Boolean(errors.code)}
                onChange={(event) => {
                  const value = event.target.value.toUpperCase();
                  onChange(draft.localId, (prev) => ({ ...prev, code: value }));
                  onClearError(draft.localId, 'code');
                }}
                dataTestId={`promoCodeInput-${index}`}
              />
            </div>
            {renderError(errors.code)}
            <div className='flex flex-row items-center gap-4'>
              <ValidationHint
                label={t('promo_code_min_characters')}
                satisfied={hasCode ? hasMinLength(draft.code) : undefined}
              />
              <ValidationHint
                label={t('promo_code_max_characters')}
                satisfied={hasCode ? hasMaxLength(draft.code) : undefined}
              />
            </div>
          </div>

          <div className='flex flex-col gap-3 py-5'>
            <div className='flex flex-col gap-1'>
              <Label text={t('duration')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
              <Label
                text={t('promo_code_duration_description')}
                type={LabelType.BODY3}
                color={ColorType.GREY6}
              />
            </div>
            <div className='flex flex-row items-start gap-2 rounded bg-warning_light px-3 py-2 w-fit'>
              <Icon
                src={ErrorWarningCircle}
                size={IconSize.SIZE_20x20}
                color={ColorType.WARNING2}
              />
              <p className='text-sm leading-5 text-grey6'>{t('promo_code_window_notice')}</p>
            </div>
            <div className='flex flex-row gap-3 w-full max-w-[440px]'>
              <div className='flex flex-col gap-2 flex-1'>
                <Label
                  text={t('discount_starts')}
                  type={LabelType.LABEL_S}
                  color={ColorType.GREY6}
                />
                <DateTimePicker
                  defaultSelectedDate={draft.validFrom ? toLocalDateString(draft.validFrom) : ''}
                  format='MM/dd/yyyy'
                  customInputType={CustomInputType.FORMINPUTDATE}
                  showDefaultDate={false}
                  customFormInputDivWidth='w-full'
                  minDate={promotionStart}
                  maxDate={draft.validUntil ?? promotionEnd}
                  onChange={(date: Date | null) => {
                    onChange(draft.localId, (prev) => ({ ...prev, validFrom: date }));
                    onClearError(draft.localId, 'validFrom');
                  }}
                />
                {renderError(errors.validFrom)}
              </div>
              <div className='flex flex-col gap-2 flex-1'>
                <div className='flex flex-row gap-1'>
                  <Label
                    text={t('discount_ends')}
                    type={LabelType.LABEL_S}
                    color={ColorType.GREY6}
                  />
                  <Label
                    text={`(${t('optional')})`}
                    type={LabelType.LABEL_S_MEDIUM}
                    color={ColorType.GREY6}
                  />
                </div>
                <DateTimePicker
                  defaultSelectedDate={draft.validUntil ? toLocalDateString(draft.validUntil) : ''}
                  format='MM/dd/yyyy'
                  customInputType={CustomInputType.FORMINPUTDATE}
                  showDefaultDate={false}
                  customFormInputDivWidth='w-full'
                  minDate={draft.validFrom ?? promotionStart}
                  maxDate={promotionEnd}
                  onChange={(date: Date | null) => {
                    if (date) {
                      date.setHours(23, 59, 59, 999);
                    }
                    onChange(draft.localId, (prev) => ({ ...prev, validUntil: date }));
                    onClearError(draft.localId, 'validUntil');
                  }}
                />
                {renderError(errors.validUntil)}
              </div>
            </div>
          </div>

          <div className='flex flex-row justify-end items-center gap-4 py-4 !border-t-0'>
            <Button
              label={t('delete_draft')}
              type={ButtonType.DESTRUCTIVE}
              size={ButtonSize.SMALL}
              onClick={() => onDelete?.(draft.localId)}
              disabled={isSaving}
              dataTestId={
                mode === 'edit' ? `deletePromoCode-${index}` : `deletePromoCodeDraft-${index}`
              }
            />
            <div className='flex flex-row items-center gap-2'>
              {mode === 'edit' && (
                <Button
                  label={t('cancel')}
                  type={ButtonType.TERTIARY}
                  size={ButtonSize.SMALL}
                  onClick={() => onCancel?.(draft.localId)}
                  disabled={isSaving}
                  dataTestId={`cancelPromoCodeEdit-${index}`}
                />
              )}
              <Button
                label={t('save_code')}
                type={ButtonType.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={() => onSave(draft)}
                isSumbit
                isLoading={isSaving}
                disabled={isSaving}
                dataTestId={`savePromoCode-${index}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoized so editing one card doesn't re-render every other card/row in the section.
export const PromoCodeCard = memo(PromoCodeCardComponent);
